import React, { useEffect, useState, type FC } from 'react';
import { useDispatch, useSelector } from '../../store';
import { useFetchPaymentMethods } from '../../hooks/useFetchPaymentMethods';
import { InteractionName } from '../../types/InteractionName';
import { type WidgetInternalProps } from '../../types/WidgetProps';
import { useFedopsLogger } from '../Provider/Fedops/FedopsProvider';
import { useBiLogger } from '../Provider/BiLogger/BiLoggerProvider';
import { selectGooglePayDto, selectIsAppDataLoaded, selectApplePayDto, selectExperiments } from '../../selectors';
import { selectHasApplePayChecksStarted } from '../../selectors/requestsState';
import { checkApHighestSupportedVersion } from '../../actions/checkApHighestSupportedVersion';
import { checkApCanMakePayments } from '../../actions/checkApCanMakePayments';
import { loadApplePayScript } from '../../actions/loadApplePayScript';
import { getGooglePayOptions } from '../../actions/googlePay/getGooglePayOptions';
import { loadExperiments } from '../../actions/loadExperiments';
import { Experiment } from '../../types/Experiment';
import { logGeneralError } from '../../utils/sentry';
import { ResourcesLoader } from '../ResourcesLoader/ResourcesLoader';
import { Container } from './elements';

export const WidgetController: FC<WidgetInternalProps> = (props) => {
  const [isAppLoaded, setAppLoaded] = useState<boolean>(false);
  const dispatch = useDispatch();
  const fedopsLogger = useFedopsLogger();
  const biLogger = useBiLogger();
  const isAppDataLoaded = useSelector(selectIsAppDataLoaded);
  const googlePayDTO = useSelector(selectGooglePayDto);
  const applePayDTO = useSelector(selectApplePayDto);
  const experiments = useSelector(selectExperiments);
  const hasApplePayChecksStarted = useSelector(selectHasApplePayChecksStarted);

  useFetchPaymentMethods(props);

  useEffect(() => {
    if (isAppDataLoaded) {
      setAppLoaded(true);
    }
  }, [isAppDataLoaded]);

  useEffect(() => {
    dispatch(loadExperiments(props.preloadingData.experiments.all()));
    fedopsLogger.interactionStarted(InteractionName.FMP);
  }, []);

  useEffect(() => {
    if (!applePayDTO || !experiments) {
      return;
    }

    // Check if Apple Pay checks have already been initiated
    if (hasApplePayChecksStarted) {
      return;
    }

    const initializeApplePay = async () => {
      // Load Apple Pay SDK (for Chrome support)
      if (experiments[Experiment.EnableApplePayInChrome] === 'true') {
        fedopsLogger.interactionStarted(InteractionName.ApplePayLoadScript);
        await dispatch(loadApplePayScript(props.demoMode));
        fedopsLogger.interactionEnded(InteractionName.ApplePayLoadScript);
      }

      dispatch(checkApHighestSupportedVersion());
      dispatch(checkApCanMakePayments());
    };

    initializeApplePay().catch((error) => {
      logGeneralError({
        message: 'Failed to initialize Apple Pay',
        payload: error,
      });
    });
  }, [applePayDTO, experiments, hasApplePayChecksStarted, dispatch, fedopsLogger, props.demoMode]);

  useEffect(() => {
    if (googlePayDTO) {
      dispatch(getGooglePayOptions({ fedopsLogger, biLogger }));
    }
  }, [biLogger, dispatch, fedopsLogger, googlePayDTO]);

  return <Container>{isAppLoaded && <ResourcesLoader />}</Container>;
};

WidgetController.displayName = 'WidgetController';
